import * as React from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { isPushAvailable } from '@3pm-source/native-connector';
import { toast } from '@/components/ui/toast';
import { markRead, type NotificationRow } from './api';
import { useNotificationFeed, FEED_KEY } from './use-notifications';
import { routeFor } from './notification-routing';
import { invalidateJobData } from './job-cache';
import { usePushStatus } from './push-status-store';

/**
 * Web-only transient toasts. Native already surfaces foreground pushes via
 * usePushRouting (and the OS shows a tray banner when backgrounded), so this is
 * disabled on native to avoid double-toasting. On the web there is no push
 * channel, so we ride the shared feed poll (no extra request), remember every
 * notification id we have already seen (seeded on first load so the existing
 * backlog never toasts), and pop a tappable toast for anything newer.
 */
export function useNotificationToasts() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pushStatus = usePushStatus((s) => s.status);
  // Web: always on. Native: only when push isn't actually delivering ('inactive'
  // — no surface, permission denied, or registration failed). While native push
  // is still resolving ('idle') we hold off to avoid double-toasting.
  const enabled = !isPushAvailable() || pushStatus === 'inactive';
  const seen = React.useRef<Set<string> | null>(null);
  const { data } = useNotificationFeed();

  // Acting on the toast marks the row read (parity with the dropdown/native tap).
  const openNotification = React.useCallback(
    (r: NotificationRow) => {
      if (!r.readAt) {
        void markRead(r.id).catch(() => {});
        qc.invalidateQueries({ queryKey: FEED_KEY });
      }
      navigate(routeFor(r.kind, r.data.jobId));
    },
    [navigate, qc],
  );

  React.useEffect(() => {
    if (!enabled || !data) return;
    const rows = data.data;

    // First successful load: seed the seen-set with the backlog and toast
    // nothing — we only want to announce notifications that arrive after mount.
    if (seen.current === null) {
      seen.current = new Set(rows.map((r) => r.id));
      return;
    }

    const fresh = rows.filter((r) => !seen.current!.has(r.id));
    if (fresh.length === 0) return;
    fresh.forEach((r) => seen.current!.add(r.id));

    // Fresh notifications are the web channel's "job changed" signal (native
    // gets the same via onPushReceived). One call refreshes every job-
    // rendering query family so an open detail/list view updates now, not on
    // the next poll.
    invalidateJobData(qc);

    // Oldest-first so the newest ends up on top of the stack.
    const ordered = [...fresh].reverse();

    // Avoid a toast storm if a burst arrives between polls.
    if (ordered.length > 3) {
      toast(`${ordered.length} new notifications`, {
        description: 'Open the bell to review them.',
      });
      return;
    }

    ordered.forEach((r) => {
      toast(r.title, {
        description: r.body,
        action: { label: 'View', onClick: () => openNotification(r) },
      });
    });
  }, [data, enabled, openNotification, qc]);
}
